import { ftClockCampaignField as ftCampaignField } from '../../shared/campaign-clock';
import { encounterField, itemsCol, posCoord } from '../../shared/array-types';
import { itemIdOf, rowScalar, statusField } from '../../encounter-map/ft3d/perception';
import { encounterShapeOrDerived } from './encounter-shape';
export { statusRemovalBefore as geometryStatusBefore } from '../statuses/expiry';
import { statusRemovalBefore, statusItemId, STATUS_IDS } from '../statuses/expiry';
type Row = Record<string, unknown>;
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
export const geometryRowId = (r: Row): string => upper(rowScalar(r, 'encounter_creature_id') ?? rowScalar(r, 'inventory_id'));
export function relocationShape(encounter: Row, doors: Row[] = []): Row {
  const shape = structuredClone(encounterShapeOrDerived(encounter));
  for (const opening of (shape.openings ?? []) as Row[]) {
    const state = doors.find(s => s.opening_id === opening.id);
    if (state) opening.passable = Number(state.door_open) === 1;
  }
  return shape;
}
/** Classify held source rows by their final destination, including paused and legacy unconnected zones.
 * A set is the ordinary single-destination transfer; a map is a bulk arrival with possibly split destinations. */
export function transferZoneRemovals(encounters: Row[], moving: ReadonlySet<string> | ReadonlyMap<string, string>): { status_id: string; before: Row }[] {
  const removed = new Map<string, { status_id: string; before: Row }>();
  for (const encounter of encounters) {
    const source = upper(encounterField(encounter, 'encounter_id'));
    const destination = (holder: Row): string => !moving.has(geometryRowId(holder)) ? source
      : 'get' in moving ? upper(moving.get(geometryRowId(holder))) : 'destination';
    const creatures = (encounter.encounter_creatures_arr ?? []) as Row[];
    const holders = [encounter, ...creatures, ...((encounter.encounter_inventories_arr ?? []) as Row[])];
    const anchors = holders.flatMap(holder => (ftCreatureStatuses(holder,'target') as Row[]).filter(s => Number(statusField(s,'status_aoe_source') ?? 0) === 1)
      .filter(s => !creatures.includes(holder) || upper(statusField(s, 'status_target_id')) === geometryRowId(holder))
      .map(s => ({ status: s, destination: destination(holder) })));
    for (const member of creatures) for (const s of ftCreatureStatuses(member,'target') as Row[]) {
      if (Number(statusField(s,'status_aoe_source') ?? 0) === 1 || statusItemId(s) === STATUS_IDS.concentrating) continue;
      const connection = upper(statusField(s,'status_connected_id'));
      if (!anchors.some(a => {
        if (a.destination === destination(member)) return false;
        const connected = upper(statusField(a.status, 'status_connected_id'));
        if (connected) return connected === connection;
        const origin = upper(statusField(a.status, 'status_origin_id') || statusField(a.status, 'status_item_id'));
        const caster = upper(statusField(a.status, 'status_caster_id'));
        return !!origin && !!caster && caster === upper(statusField(s, 'status_caster_id'))
          && [statusField(s, 'status_origin_id'), statusItemId(s)].some(id => upper(id) === origin);
      })) continue;
      const id = upper(statusField(s,'status_id'));
      if (id) removed.set(id,{status_id:id,before:statusRemovalBefore(s)});
    }
  }
  return [...removed.values()];
}

const list=(v:unknown):Row[]=>Array.isArray(v)?v as Row[]:[];
const scalar=rowScalar;
/** Preserve the encounter display inputs and authored room labels in the GM picker. */
export function relocationContext(encounters: Row[], source: Row, destination: Row): Row {
  const shape = relocationShape(destination);
  const draft = (shape.authoring as Row | undefined)?.sceneBuilderRoomContentDraft as Row | undefined;
  const authored = list(draft?.rooms);
  const display = (row: Row): { encounters: Row } => ({ encounters: Object.fromEntries(
    ['encounter_zone_prefix', 'encounter_zone_number', 'location_zone_prefix'].map(key => [key, encounterField(row, key)])) });
  return {
    ok: true, sourceEncounterId: upper(encounterField(source, 'encounter_id')), destinationEncounterId: upper(encounterField(destination, 'encounter_id')),
    encounters: encounters.map(row => ({ id: upper(encounterField(row, 'encounter_id')), name: encounterField(row, 'encounter_name'),
      zone_parts: display(row), location: encounterField(row, 'encounter_location_name'),
      private: Number(encounterField(row, 'encounter_private') ?? 0) === 1, explored: Number(encounterField(row, 'encounter_explored') ?? 0) === 1 })),
    roomZoneParts: display(destination),
    rooms: list(shape.boxes).map(box => {
      const room = authored.find(room => room.roomId === box.id);
      return { id: box.id, name: room?.name ? String(room.name) : String(box.id).replace(/[-_]/g, ' '), mapKey: String(room?.mapKey ?? ''), floor: Number(box.floor ?? 0) };
    }),
    roster: list(source.encounter_creatures_arr).map(row => ({ id: geometryRowId(row),
      name: String(scalar(row, 'player_name') ?? scalar(row, 'encounter_creature_name') ?? itemsCol(row, 'item_name') ?? 'Creature'),
      pc: itemsCol(row, 'item_type') === 'player_character', thumb: null, dead: Number(scalar(row, 'encounter_creature_dead') ?? 0) !== 0,
      summonOf: upper(scalar(row, 'controller_encounter_creature_id')) })),
  };
}

function requestedDestination(data: Row, f: Row): string {
  if (data.action === 'haven-return') return upper(f.destination_encounter_id);
  const route = String(data.relocation_route ?? '');
  const campaign = f.campaign as Row;
  if (route.includes('stronghold')) return itemIdOf(ftCampaignField(campaign,'campaign_stronghold_encounter_id'));
  if (route.includes('campsite')) return itemIdOf(ftCampaignField(campaign,'campaign_campsite_encounter_id'));
  if (route === 'gm_pull_all_creatures_from_encounter_inc.php') return itemIdOf(ftCampaignField(campaign,'encounter_loaded'));
  return upper(data.go_to_inventory_link ?? data.destination_encounter_id ?? data.go_to_encounter ?? data.encounter_loaded ?? data.encounter_id);
}
/** The read carries only saved parents. An inventory UUID names an arrival anchor, never a room. */
function arrivalRows(data: Row, f: Row): Row[] {
  const rows = list(f.arrival_rows);
  return rows.length && geometryRowId(rows[0]!) === requestedDestination(data, f) ? rows : [];
}
export function relocationDestination(data: Row, f: Row): string {
  const rows = arrivalRows(data, f), parent = rows[rows.length - 1];
  return parent ? upper(scalar(parent, 'possess_id') ?? scalar(parent, 'encounter_id')) : requestedDestination(data, f);
}
/** Contained anchors arrive at their outer spatial parent; explicit grid moves retain their own point. */
export function relocationPoint(data: Row, f: Row): { x: number; y: number; z: number } | undefined {
  if (['x', 'y', 'z'].every(key => data[key] !== undefined && Number.isFinite(Number(data[key]))))
    return { x: Number(data.x), y: Number(data.y), z: Number(data.z) };
  const rows = arrivalRows(data, f), anchor = rows[rows.length - 1];
  return anchor ? { x: posCoord(anchor, 'x_pos') ?? 0, y: posCoord(anchor, 'y_pos') ?? 0, z: posCoord(anchor, 'z_pos') ?? 0 } : undefined;
}
export function relocationSelection(data: Row, f: Row): string[] {
  if (data.action === 'haven-return') return [upper(data.actor_id)].filter(Boolean);
  const rows = list(f.creatures), campaign = f.campaign as Row, route = String(data.relocation_route ?? '');
  if (Array.isArray(data.creature_ids)) return data.creature_ids.map(upper);
  if (data.player_item_id) return rows.filter(r => upper(scalar(r,'item_id')) === upper(data.player_item_id)).map(geometryRowId);
  if (route.startsWith('gm_push_party_')) {
    const active = new Set(list(campaign.campaign_players).filter(r => Number(scalar(r,'player_character_inactive') ?? 0) !== 1).map(r => upper(scalar(r,'item_id'))));
    return rows.filter(r => active.has(upper(scalar(r,'item_id'))) && Number(scalar(r,'encounter_creature_dead') ?? 0) === 0).map(geometryRowId);
  }
  if (route === 'gm_push_all_to_encounter_inc.php' || route === 'gm_pull_all_creatures_from_encounter_inc.php') {
    const source = upper(route.startsWith('gm_pull') ? data.encounter_loaded : data.encounter_id);
    const inactive = new Set(list(campaign.campaign_players).filter(r => Number(scalar(r,'player_character_inactive') ?? 0) === 1).map(r => upper(scalar(r,'item_id'))));
    return rows.filter(r => upper(scalar(r,'encounter_id')) === source && Number(scalar(r,'encounter_creature_private') ?? 0) !== 1 && !inactive.has(upper(scalar(r,'item_id')))).map(geometryRowId);
  }
  const target = data.target_id ?? data.encounter_creature_id ?? data.caster_id;
  return (Array.isArray(target) ? target : String(target ?? '').split(',')).map(upper).filter(Boolean);
}
